import { Fragment, useState, useMemo, useRef, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Download, ChevronUp, ChevronDown, CheckCircle2 } from 'lucide-react';
import { STATUS_COLORS } from '../config/colors';
import { Button } from './ui/button';

interface Column {
  key: string;
  label: string | ReactNode;
  align?: 'left' | 'center' | 'right';
  /** Allow long cell text to wrap instead of forcing a single line. */
  wrap?: boolean;
}

interface DataTableProps {
  columns: Column[];
  data: Record<string, unknown>[];
  title?: string | ReactNode;
  onExport?: () => void;
  equalWidthColumns?: boolean;
  hideExport?: boolean;
  customColumnWidths?: Record<string, { minWidth?: number; maxWidth?: number }>;
  maxRows?: number; // Maximum number of rows to display before scrolling (default: 10)
  headerRightContent?: ReactNode; // Custom content to render on the right side of the header (replaces export button)
  defaultSort?: { column: string; direction: 'asc' | 'desc' }; // Default sort column and direction
  /** When false, headers are not clickable; defaultSort still applies as a fixed order. */
  sortable?: boolean;
  hoverColor?: 'green' | 'blue' | 'purple' | 'red' | 'yellow'; // Hover border and shadow color (default: green)
  /** When true, rows do not get hover border/glow/text tint (status accents still apply). */
  disableRowHover?: boolean;
  emptyMessage?: string; // Shown in the table body when there are no rows
  onRowClick?: (row: Record<string, unknown>, index: number) => void; // Callback when a row is clicked
  selectedRowId?: string | null; // ID of the currently selected row (for persistent highlighting)
  rowIdKey?: string; // Key in row data to use for row ID comparison (default: 'id')
  /** Row (by rowIdKey) whose expanded body is shown under it. */
  expandedRowId?: string | null;
  /** Body for the expanded row; return null for rows that do not expand. */
  renderExpandedRow?: (row: Record<string, unknown>) => ReactNode | null;
}

type SortDirection = 'asc' | 'desc' | null;

export default function DataTable({ columns, data, title, onExport, equalWidthColumns = false, hideExport = false, customColumnWidths, maxRows = 10, headerRightContent, defaultSort, sortable = true, hoverColor = 'green', disableRowHover = false, emptyMessage = 'No data preview', onRowClick, selectedRowId, rowIdKey = 'id', expandedRowId, renderExpandedRow }: DataTableProps) {
  const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);
  const [hoveredUtilisationCell, setHoveredUtilisationCell] = useState<number | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number } | null>(null);
  const [hoveredCheckmark, setHoveredCheckmark] = useState<number | null>(null);
  const [checkmarkTooltipPosition, setCheckmarkTooltipPosition] = useState<{ x: number; y: number } | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const checkmarkTooltipRef = useRef<HTMLDivElement>(null);

  const [sortColumn, setSortColumn] = useState<string | null>(defaultSort?.column || null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(defaultSort?.direction || null);

  // Handle column header click for sorting
  // Cycle: null -> desc -> asc -> null -> desc -> ...
  const handleSort = (columnKey: string) => {
    if (sortColumn === columnKey) {
      // Same column: cycle through desc -> asc -> null
      if (sortDirection === 'desc') {
        setSortDirection('asc');
      } else if (sortDirection === 'asc') {
        setSortDirection(null);
        setSortColumn(null); // Clear sort column when disabling
      } else {
        setSortDirection('desc');
      }
    } else {
      // New column, start with descending
      setSortColumn(columnKey);
      setSortDirection('desc');
    }
  };

  // Sort the data based on current sort column and direction
  const getSortValue = (row: Record<string, unknown>, columnKey: string) => {
    const customSortKey = row[`${columnKey}_sortKey`];
    if (customSortKey !== undefined) {
      return customSortKey;
    }
    // For overallScore, prefer the numeric value if available
    if (columnKey === 'overallScore' && row._overallScoreValue !== undefined) {
      return row._overallScoreValue;
    }
    return row[columnKey];
  };

  const sortedData = useMemo(() => {
    if (!sortColumn || !sortDirection) {
      return data;
    }

    // Separate sticky rows (like TOTAL) from regular rows
    const stickyRows = data.filter(row => row._isStickyRow === true);
    const regularRows = data.filter(row => row._isStickyRow !== true);

    // Sort only the regular rows
    const sortedRegularRows = [...regularRows].sort((a, b) => {
      const aValue = getSortValue(a, sortColumn);
      const bValue = getSortValue(b, sortColumn);

      // Handle numeric values
      const aNum = parseFloat(String(aValue));
      const bNum = parseFloat(String(bValue));
      const isNumeric = !isNaN(aNum) && !isNaN(bNum) && isFinite(aNum) && isFinite(bNum);

      if (isNumeric) {
        return sortDirection === 'asc' ? aNum - bNum : bNum - aNum;
      }

      // Handle string values
      const aStr = String(aValue || '').toLowerCase();
      const bStr = String(bValue || '').toLowerCase();

      if (sortDirection === 'asc') {
        return aStr.localeCompare(bStr);
      } else {
        return bStr.localeCompare(aStr);
      }
    });

    // Append sticky rows at the end (they should always stay at bottom)
    return [...sortedRegularRows, ...stickyRows];
  }, [data, sortColumn, sortDirection]);

  // Do not reset sort when data or defaultSort changes. Default sort is applied only once via
  // initial state when the component mounts (user first enters the page). User's column sort
  // choice is preserved when sorting by other columns or when data updates (e.g. filters).

  // Calculate visual length of text (estimating character width)
  const getVisualLength = (text: string | ReactNode): number => {
    if (typeof text === 'string') {
      return text.length;
    }
    // For ReactNode, estimate based on common patterns
    if (typeof text === 'object' && text !== null) {
      // Check if it's a div with children (like vehicle_id with two lines)
      if ('props' in text && text.props && text.props.children) {
        const children = Array.isArray(text.props.children) 
          ? text.props.children 
          : [text.props.children];
        return Math.max(...children.map((child: ReactNode) => {
          if (typeof child === 'string') return child.length;
          if (child && typeof child === 'object' && 'props' in child && child.props.children) {
            return String(child.props.children).length;
          }
          return 0;
        }));
      }
    }
    return 10; // Default estimate
  };

  // Calculate content-based column widths
  const columnWidths = useMemo(() => {
    // If customColumnWidths is provided, always calculate widths (override equalWidthColumns)
    if (equalWidthColumns && !customColumnWidths) return {};
    if (data.length === 0) return {};
    
    const widths: Record<string, number> = {};
    
    columns.forEach(column => {
      // Get header text length
      const headerLength = typeof column.label === 'string' 
        ? column.label.length 
        : getVisualLength(column.label);
      
      // Find max content length in data
      let maxContentLength = headerLength;
      
      data.forEach(row => {
        const value = row[column.key];
        if (value !== null && value !== undefined) {
          if (typeof value === 'object' && value !== null && 'props' in value) {
            // ReactNode - estimate visual length
            const visualLen = getVisualLength(value as ReactNode);
            maxContentLength = Math.max(maxContentLength, visualLen);
          } else {
            const valueStr = String(value);
            maxContentLength = Math.max(maxContentLength, valueStr.length);
          }
        }
      });
      
      // Estimate pixel width: ~7px per character for text-sm font
      // Add padding (px-2 = 8px each side = 16px total)
      // Add extra space for progress bars (utilisation_rate, overallScore, co2e_per_mile, overall_util_pct)
      const hasProgressBar = column.key === 'utilisation_rate' || column.key === 'overallScore' || column.key === 'performance_rank' || column.key === 'co2e_per_mile' || column.key === 'overall_util_pct';
      const extraSpace = hasProgressBar ? 80 : 0; // Extra space for progress bar
      const estimatedPixels = (maxContentLength * 7) + 16 + extraSpace;
      
      widths[column.key] = estimatedPixels;
    });
    
    // Normalize to percentages
    const totalPixels = Object.values(widths).reduce((sum, w) => sum + w, 0);
    if (totalPixels === 0) return {};
    
    const percentages: Record<string, number> = {};
    columns.forEach(column => {
      if (widths[column.key]) {
        let percentage = (widths[column.key] / totalPixels) * 100;
        
        // Apply min/max constraints based on column type
        let minWidth = 6;
        let maxWidth = 20;
        
        // Check for custom column width constraints first
        if (customColumnWidths && customColumnWidths[column.key]) {
          minWidth = customColumnWidths[column.key].minWidth ?? minWidth;
          maxWidth = customColumnWidths[column.key].maxWidth ?? maxWidth;
        } else {
        // Special handling for multi-line pair columns
        if (column.key === 'vehiclePair' || column.key === 'vehicleTypePair') {
          minWidth = 10;
          maxWidth = 14;
        } else if (column.key === 'vehicle_id' || column.key === 'vehicle_type' || column.key === 'station_name' || column.key === 'station') {
          minWidth = 8;
          maxWidth = 12;
        } else if (column.key === 'utilisation_rate' || column.key === 'overallScore' || column.key === 'overall_util_pct') {
          minWidth = 11;
          maxWidth = 15;
        } else if (column.key === 'total_miles') {
          minWidth = 6;
          maxWidth = 10;
        } else if (column.key === 'total_days') {
          minWidth = 8;
          maxWidth = 12;
          }
        }
        
        percentage = Math.max(minWidth, Math.min(maxWidth, percentage));
        percentages[column.key] = percentage;
      }
    });
    
    // Normalize to sum to 100%, then re-apply maxWidth constraints
    const totalPercentage = Object.values(percentages).reduce((sum, p) => sum + p, 0);
    if (totalPercentage > 0) {
      const normalized: Record<string, number> = {};
      
      // First normalize
      columns.forEach(column => {
        if (percentages[column.key]) {
          normalized[column.key] = (percentages[column.key] / totalPercentage) * 100;
        }
      });
      
      // Then re-apply maxWidth constraints to prevent exceeding limits
      columns.forEach(column => {
        if (normalized[column.key]) {
          let maxWidth = 20;
          if (customColumnWidths && customColumnWidths[column.key]) {
            maxWidth = customColumnWidths[column.key].maxWidth ?? maxWidth;
          }
          if (normalized[column.key] > maxWidth) {
            normalized[column.key] = maxWidth;
          }
        }
      });
      
      return normalized;
    }
    
    return percentages;
  }, [columns, data, customColumnWidths, equalWidthColumns]);

  // Calculate column width based on content
  const getColumnWidth = (columnKey: string, totalColumns: number) => {
    // If customColumnWidths is provided, always use calculated widths (override equalWidthColumns)
    if (equalWidthColumns && !customColumnWidths) {
      // Use fixed pixel width for equal columns to enable horizontal scrolling
      // Minimum 150px per column, but can be wider if needed
      return '150px';
    }
    // Use content-based width if available
    if (columnWidths[columnKey]) {
      return `${columnWidths[columnKey].toFixed(2)}%`;
    }
    
    // Fallback to fixed widths for special cases
    if (columnKey === 'vehiclePair' || columnKey === 'vehicleTypePair') {
      return totalColumns <= 7 ? '16%' : totalColumns <= 10 ? '14%' : '12%';
    }
    
    // Default fallback
    return `${Math.floor(100 / totalColumns)}%`;
  };
  // Default export function
  const handleDefaultExport = () => {
    // Default export: convert table data to CSV
    const csvContent = [
      // Header row
      columns.map(col => typeof col.label === 'string' ? col.label : col.key).join(','),
      // Data rows
      ...data.map(row => 
        columns.map(col => {
          const value = row[col.key];
          // Handle ReactNode values by extracting text
          if (value && typeof value === 'object' && 'props' in value) {
            return '"' + String(value).replace(/"/g, '""') + '"';
          }
          // Handle strings with commas
          const strValue = String(value || '');
          return strValue.includes(',') ? `"${strValue.replace(/"/g, '""')}"` : strValue;
        }).join(',')
      )
    ].join('\n');
    
    // Create download link
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${title || 'table'}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="bg-card rounded-lg shadow-sm overflow-visible border border-border w-full max-w-full">
      {(!hideExport || title || headerRightContent) && (
        <div className={`px-6 py-4 ${title ? 'border-b border-border' : ''} flex items-center ${title || headerRightContent ? 'justify-between' : 'justify-end'} bg-transparent`}>
          {title && <h3 className="text-lg font-semibold text-white flex-1 min-w-0">{title}</h3>}
          {headerRightContent ? (
            headerRightContent
          ) : !hideExport && (
            <Button
              type="button"
              variant="secondary"
              size="md"
              onClick={onExport || handleDefaultExport}
            >
              <Download className="w-4 h-4" />
              <span>Export</span>
            </Button>
          )}
        </div>
      )}
      <div 
        className={`overflow-y-auto ${equalWidthColumns ? 'overflow-x-auto' : 'overflow-x-visible'}`}
        style={{ 
          width: '100%',
          // Calculate max height: header (~40px) + (maxRows * row height ~42px) + some padding
          // Row height is approximately 40-45px depending on content
          maxHeight: `${40 + (maxRows * 42)}px`,
          padding: '8px', // Add padding to allow glow to extend beyond rounded corners
          paddingTop: '0', // Remove top padding so header can stick properly
          paddingBottom: '8px' // Keep bottom padding for sticky row extension
        }}
      >
        <table 
          className="divide-y divide-border" 
          style={{ 
            tableLayout: 'fixed', 
            position: 'relative',
            width: equalWidthColumns ? 'max-content' : '100%',
            minWidth: equalWidthColumns ? `${columns.length * 150}px` : '100%'
          }}
        >
          <thead className="sticky top-0 z-10 bg-secondary border-b border-accent" style={{ marginTop: '-8px', paddingTop: '8px' }}>
            <tr>
              {columns.map((column) => {
                const isSorted = sortable && sortColumn === column.key;
                const isAsc = sortDirection === 'asc';
                return (
                <th
                  key={column.key}
                    onClick={sortable ? () => handleSort(column.key) : undefined}
                    className={`px-2 py-1.5 text-sm font-medium tracking-tight bg-secondary ${
                      sortable
                        ? 'cursor-pointer select-none'
                        : 'cursor-default'
                    } ${
                      typeof column.label === 'string' ? 'whitespace-normal break-words' : ''
                    } ${
                      isSorted ? 'text-primary' : 'text-foreground'
                    } ${
                    column.align === 'right' ? 'text-right' : column.align === 'center' ? 'text-center' : 'text-left'
                  }`}
                  style={{
                    width: getColumnWidth(column.key, columns.length)
                  }}
                >
                    <div className={`flex items-start gap-1 ${
                      column.align === 'right' ? 'justify-end' : column.align === 'center' ? 'justify-center' : 'justify-start'
                    }`}>
                      <div className={typeof column.label === 'string' ? '' : 'text-center'}>
                        {typeof column.label === 'string' ? <span>{column.label}</span> : column.label}
                      </div>
                      {isSorted && (
                        <span className="inline-flex mt-0.5">
                          {isAsc ? (
                            <ChevronUp className="w-3 h-3" />
                          ) : (
                            <ChevronDown className="w-3 h-3" />
                          )}
                        </span>
                      )}
                    </div>
                </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="bg-card divide-y divide-border relative" style={{ overflow: 'visible' }}>
            {sortedData.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-3 py-8 text-center text-sm text-muted-foreground"
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : sortedData.map((row, idx) => {
              const isHovered = !disableRowHover && hoveredRowIndex === idx;
              const isHighlighted = row._isHighlighted === true;
              const isStickyRow = row._isStickyRow === true;
              const rowAccentRaw = String(row._rowAccent || '').toLowerCase();
              const rowAccent =
                rowAccentRaw === 'amber' || rowAccentRaw === 'red' || rowAccentRaw === 'green'
                  ? rowAccentRaw
                  : null;
              const rowId = row[rowIdKey];
              const isSelected = selectedRowId !== null && selectedRowId !== undefined && rowId === selectedRowId;
              const shouldHighlight = isSelected;
              
              // Determine border color based on hoverColor prop
              const borderTokenMap = {
                green: 'success',
                blue: 'ring',
                purple: 'brand',
                red: 'destructive',
                yellow: 'warning'
              };
              const borderToken = borderTokenMap[hoverColor] || 'success';
              const borderColor = `rgb(var(--${borderToken}))`;
              const accentColorMap: Record<string, { border: string; bg: string; inset: string }> = {
                amber: {
                  border: STATUS_COLORS.warning,
                  bg: 'rgba(245, 158, 11, 0.08)',
                  inset: 'inset 0 0 0 1px rgba(245, 158, 11, 0.18)',
                },
                red: {
                  border: STATUS_COLORS.destructive,
                  bg: 'rgba(239, 68, 68, 0.08)',
                  inset: 'inset 0 0 0 1px rgba(239, 68, 68, 0.18)',
                },
                green: {
                  border: STATUS_COLORS.success,
                  bg: 'rgba(34, 197, 94, 0.08)',
                  inset: 'inset 0 0 0 1px rgba(34, 197, 94, 0.18)',
                },
              };
              const accentStyle = rowAccent ? accentColorMap[rowAccent] : null;
              // Selected row: thicker border, stronger glow, distinct background so it's clear which row is selected vs hovered
              const borderLeft = shouldHighlight
                ? isSelected
                  ? `4px solid ${borderColor}`
                  : `2px solid ${borderColor}`
                : accentStyle
                  ? `3px solid ${accentStyle.border}`
                  : '2px solid transparent';
              const rowBg = shouldHighlight && isStickyRow
                ? 'rgba(31, 41, 55, 0.95)'
                : isSelected
                ? 'rgba(34, 197, 94, 0.12)' // green tint when selected
                : isHovered
                ? 'rgba(55, 65, 81, 0.5)'
                : isStickyRow
                ? 'rgb(var(--card))'
                : accentStyle
                  ? accentStyle.bg
                  : undefined;
              const rowShadow = isStickyRow
                ? '0 -4px 12px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.05)'
                : isSelected
                ? `0 0 14px rgb(var(--${borderToken}) / 0.38), inset 0 0 0 1px rgb(var(--${borderToken}) / 0.19)`
                : shouldHighlight
                ? `0 0 8px rgb(var(--${borderToken}) / 0.25)`
                : accentStyle
                  ? accentStyle.inset
                  : undefined;
              
              const expandedBody =
                  renderExpandedRow && expandedRowId != null && rowId === expandedRowId
                    ? renderExpandedRow(row)
                    : null;
              return (
                <Fragment key={idx}>
                <tr 
                  aria-expanded={renderExpandedRow ? expandedBody != null : undefined}
                  className={`group transition-all cursor-pointer ${
                    isStickyRow ? 'border-t-2 border-accent font-semibold' : ''
                  } ${isSelected ? 'ring-1 ring-inset ring-success/30' : ''}`}
                  style={{
                    borderLeft,
                    backgroundColor: rowBg,
                    boxShadow: rowShadow,
                    ...(isStickyRow ? {
                      position: 'sticky',
                      bottom: 0,
                      zIndex: 10,
                      backdropFilter: 'blur(4px)' // Slight blur effect for better separation
                    } : {})
                  }}
                  onMouseEnter={() => {
                    if (!disableRowHover) setHoveredRowIndex(idx);
                  }}
                  onMouseLeave={() => {
                    if (!disableRowHover) setHoveredRowIndex(null);
                  }}
                  onClick={() => {
                    // Check if row has _onClick handler (from data) or use prop handler
                    if (row._onClick) {
                      (row._onClick as () => void)();
                    } else if (onRowClick) {
                      onRowClick(row, idx);
                    }
                  }}
                >
                  {columns.map((column) => {
                    const isOverallScore = column.key === 'overallScore';
                    const isUtilisationRate = column.key === 'utilisation_rate';
                    const isPerformanceRank = column.key === 'performance_rank';
                    const isCo2ePerMile = column.key === 'co2e_per_mile';
                    const isOverallUtilPct = column.key === 'overall_util_pct';
                    const scoreValue = isOverallScore && row._overallScoreValue !== undefined ? (row._overallScoreValue as number) : 
                                      isUtilisationRate && row._utilisationRateValue !== undefined ? (row._utilisationRateValue as number) :
                                      isPerformanceRank && row._rankValue !== undefined ? (row._rankValue as number) :
                                      isCo2ePerMile && row._co2ePerMileValue !== undefined ? (row._co2ePerMileValue as number) :
                                      isOverallUtilPct && row._overallUtilValue !== undefined ? (row._overallUtilValue as number) : null;
                    const scoreColor = isOverallScore && row._overallScoreColor ? (row._overallScoreColor as string) :
                                     isUtilisationRate && row._utilisationRateColor ? (row._utilisationRateColor as string) :
                                     isPerformanceRank && row._rankColor ? (row._rankColor as string) :
                                     isCo2ePerMile && row._co2ePerMileColor ? (row._co2ePerMileColor as string) :
                                     isOverallUtilPct && row._overallUtilColor ? (row._overallUtilColor as string) : null;
                    const hasProgressBar = (isOverallScore || isUtilisationRate || isPerformanceRank || isCo2ePerMile || isOverallUtilPct) && scoreValue !== null && scoreColor;
                    
                    // Determine which category the current value falls into for tooltip highlighting
                    const getCategory = () => {
                      if (scoreValue === null) return null;
                      if (isUtilisationRate || isOverallScore || isOverallUtilPct) {
                        if (scoreValue >= 70) return 'green';
                        if (scoreValue >= 40) return 'yellow';
                        return 'red';
                      }
                      if (isPerformanceRank) {
                        if (scoreValue >= 70) return 'green'; // Busier
                        if (scoreValue >= 40) return 'yellow'; // Medium
                        return 'red'; // Less busier
                      }
                      if (isCo2ePerMile) {
                        // For CO2e, lower is better, so we need to check the actual value
                        // The scoreValue is already normalized (lower CO2e = higher score)
                        if (scoreValue >= 70) return 'green'; // Low emissions
                        if (scoreValue >= 40) return 'yellow'; // Medium emissions
                        return 'red'; // High emissions
                      }
                      return null;
                    };
                    const currentCategory = getCategory();
                    
                    // Determine text color and glow based on hoverColor prop
                    const getTextColorClass = () => {
                      if (isHighlighted) {
                        return 'text-warning';
                      }
                      if (shouldHighlight) {
                        // Use hoverColor for text color
                        const textColorMap = {
                          green: 'text-success',
                          blue: 'text-ring',
                          purple: 'text-brand',
                          red: 'text-destructive',
                          yellow: 'text-warning'
                        };
                        return textColorMap[hoverColor] || 'text-success';
                      }
                      return 'text-foreground';
                    };
                    
                    // Determine glow color for inline style
                    const getGlowStyle = () => {
                      if (isHighlighted) {
                        return { textShadow: '0 0 8px rgba(250,204,21,0.8)' };
                      }
                      if (shouldHighlight) {
                        const glowColorMap = {
                          green: 'rgba(34,197,94,0.6)',
                          blue: 'rgba(59,130,246,0.6)',
                          purple: 'rgba(168,85,247,0.6)',
                          red: 'rgba(239,68,68,0.6)',
                          yellow: 'rgba(234,179,8,0.6)'
                        };
                        const glowColor = glowColorMap[hoverColor] || 'rgba(34,197,94,0.6)';
                        return { textShadow: `0 0 4px ${glowColor}` };
                      }
                      return {};
                    };
                    
                    return (
                  <td
                    key={column.key}
                        className={`px-2 text-sm transition-all ${
                          isStickyRow ? 'py-3 pb-4' : 'py-2' // Extra padding-bottom for sticky row to cover scrolled content
                        } ${
                          column.wrap
                            ? 'whitespace-normal break-words'
                            : column.key === 'vehiclePair' || column.key === 'vehicleTypePair' || column.key === 'vehicle_id'
                              ? ''
                              : 'whitespace-nowrap'
                        } ${getTextColorClass()} ${
                      column.align === 'right' ? 'text-right' : column.align === 'center' ? 'text-center' : 'text-left'
                    }`}
                    style={{
                      // Sticky row background takes priority to ensure full coverage
                      backgroundColor: isStickyRow
                        ? 'rgb(var(--card))' // Ensure cells have same background as row for full coverage
                        : hasProgressBar && scoreColor 
                        ? (() => {
                            // Handle both hex and rgb(r, g, b) formats
                            if (scoreColor.startsWith('#')) {
                              // Hex color: append alpha channel (25 hex = ~15% opacity)
                              return `${scoreColor}25`;
                            } else if (scoreColor.startsWith('rgb(')) {
                              // RGB color: convert to rgba with 15% opacity
                              return scoreColor.replace('rgb(', 'rgba(').replace(')', ', 0.15)');
                            }
                            return scoreColor;
                          })()
                        : undefined,
                      overflow: hasProgressBar ? 'visible' : undefined,
                      position: hasProgressBar ? 'relative' : undefined,
                      ...getGlowStyle()
                    }}
                  >
                        {hasProgressBar ? (
                          <div 
                            className="relative group flex items-center gap-1.5 justify-end"
                            style={{ 
                              minHeight: '24px', 
                              height: '24px',
                              zIndex: hoveredUtilisationCell === idx ? 10 : 1
                            }}
                            onMouseEnter={(e) => {
                              if (isUtilisationRate || isCo2ePerMile || isPerformanceRank || isOverallScore || isOverallUtilPct) {
                                setHoveredUtilisationCell(idx);
                                // Calculate tooltip position relative to viewport
                                const rect = e.currentTarget.getBoundingClientRect();
                                setTooltipPosition({
                                  x: rect.left + rect.width / 2,
                                  y: rect.top
                                });
                              }
                            }}
                            onMouseLeave={() => {
                              if (isUtilisationRate || isCo2ePerMile || isPerformanceRank || isOverallScore || isOverallUtilPct) {
                                setHoveredUtilisationCell(null);
                                setTooltipPosition(null);
                              }
                            }}
                          >
                            <div className="relative text-right" style={{ height: '20px', width: '45px', minWidth: '45px', maxWidth: '45px', overflow: 'visible', flexShrink: 0 }}>
                              <span className={`font-medium absolute right-0 top-0 transition-all duration-300 ${
                                isHighlighted
                                  ? 'text-warning drop-shadow-glow-warning-lg'
                                  : 'text-foreground'
                              } ${
                                hoveredUtilisationCell === idx 
                                  ? 'text-sm font-semibold leading-tight scale-102' 
                                  : 'text-sm leading-tight'
                              }`} style={{
                                transformOrigin: 'right center'
                              }}>
                                {row[column.key] as ReactNode}
                              </span>
                            </div>
                            <div className="flex-1 bg-secondary rounded-full overflow-visible min-w-15 max-w-25 flex-shrink-0 relative" style={{ height: '16px', flexShrink: 0 }}>
                              <div
                                className="absolute rounded-full transition-all duration-300"
                                style={{
                                  width: `${Math.min(100, Math.max(0, scoreValue))}%`,
                                  backgroundColor: (() => {
                                    // Use a contrasting color for the progress bar
                                    // For low scores (red), use a light color that contrasts with the dark red background
                                    if (scoreValue !== null && scoreValue < 40) {
                                      // Low score (red): use white/light gray for maximum contrast against dark red background
                                      return 'rgb(var(--warning))'; // Bright amber that stands out against dark red background
                                    } else if (scoreValue !== null && scoreValue < 70) {
                                      // Medium score (yellow): use the score color (already visible)
                                      return scoreColor;
                                    } else {
                                      // High score (green): use the score color (already visible)
                                      return scoreColor;
                                    }
                                  })(),
                                  height: hoveredUtilisationCell === idx ? '14px' : '12px',
                                  top: hoveredUtilisationCell === idx ? '0px' : '2px',
                                  left: '0px',
                                }}
                              />
                            </div>
                            {/* Show checkmark after progress bar if flag is set */}
                            {!!row._showCheckmark && (
                              <div
                                className="relative"
                                onMouseEnter={(e) => {
                                  setHoveredCheckmark(idx);
                                  const rect = e.currentTarget.getBoundingClientRect();
                                  setCheckmarkTooltipPosition({
                                    x: rect.left + rect.width / 2,
                                    y: rect.top
                                  });
                                }}
                                onMouseLeave={() => {
                                  setHoveredCheckmark(null);
                                  setCheckmarkTooltipPosition(null);
                                }}
                              >
                                <CheckCircle2 className="w-4 h-4 text-success flex-shrink-0 drop-shadow-glow-success-sm ml-1 cursor-help" />
                              </div>
                            )}
                            {/* Tooltip explaining color scale - only shows on hover */}
                            {hoveredUtilisationCell === idx && tooltipPosition && createPortal(
                              <div 
                                ref={tooltipRef}
                                className="fixed px-3 py-2 bg-background text-white text-xs font-medium rounded-lg shadow-xl pointer-events-none z-float border border-border whitespace-nowrap"
                                style={{
                                  left: `${tooltipPosition.x}px`,
                                  top: `${tooltipPosition.y - 8}px`,
                                  transform: 'translate(-50%, -100%)'
                                }}
                              >
                                {isUtilisationRate && (
                                  <div className="space-y-1">
                                    <div className="font-semibold mb-1.5">
                                      {title === 'Station Conversion Summary' || title === 'Vehicle Type Summary' ? 'EV %:' : 'Utilisation Rate:'}
                                    </div>
                                    <div className={`flex items-center gap-2 transition-opacity ${currentCategory === 'green' ? 'opacity-100' : 'opacity-40'}`}>
                                      <span className="w-3 h-3 rounded-full bg-success"></span> High (70-100%)
                                    </div>
                                    <div className={`flex items-center gap-2 transition-opacity ${currentCategory === 'yellow' ? 'opacity-100' : 'opacity-40'}`}>
                                      <span className="w-3 h-3 rounded-full bg-warning"></span> Medium (40-70%)
                                    </div>
                                    <div className={`flex items-center gap-2 transition-opacity ${currentCategory === 'red' ? 'opacity-100' : 'opacity-40'}`}>
                                      <span className="w-3 h-3 rounded-full bg-destructive"></span> Low (0-40%)
                                    </div>
                                  </div>
                                )}
                                {isCo2ePerMile && (
                                  <div className="space-y-1">
                                    <div className="font-semibold mb-1.5">CO2e per Mile:</div>
                                    <div className={`flex items-center gap-2 transition-opacity ${currentCategory === 'green' ? 'opacity-100' : 'opacity-40'}`}>
                                      <span className="w-3 h-3 rounded-full bg-success"></span> Low emissions (efficient)
                                    </div>
                                    <div className={`flex items-center gap-2 transition-opacity ${currentCategory === 'yellow' ? 'opacity-100' : 'opacity-40'}`}>
                                      <span className="w-3 h-3 rounded-full bg-warning"></span> Medium emissions
                                    </div>
                                    <div className={`flex items-center gap-2 transition-opacity ${currentCategory === 'red' ? 'opacity-100' : 'opacity-40'}`}>
                                      <span className="w-3 h-3 rounded-full bg-destructive"></span> High emissions (inefficient)
                                    </div>
                                  </div>
                                )}
                                {isPerformanceRank && (
                                  <div className="space-y-1">
                                    <div className="font-semibold mb-1.5">Busy Rank:</div>
                                    <div className={`flex items-center gap-2 transition-opacity ${currentCategory === 'green' ? 'opacity-100' : 'opacity-40'}`}>
                                      <span className="w-3 h-3 rounded-full bg-success"></span> Busier
                                    </div>
                                    <div className={`flex items-center gap-2 transition-opacity ${currentCategory === 'yellow' ? 'opacity-100' : 'opacity-40'}`}>
                                      <span className="w-3 h-3 rounded-full bg-warning"></span> Medium
                                    </div>
                                    <div className={`flex items-center gap-2 transition-opacity ${currentCategory === 'red' ? 'opacity-100' : 'opacity-40'}`}>
                                      <span className="w-3 h-3 rounded-full bg-destructive"></span> Less busier
                                    </div>
                                  </div>
                                )}
                                {isOverallScore && (
                                  <div className="space-y-1">
                                    <div className="font-semibold mb-1.5">Overall Score:</div>
                                    <div className={`flex items-center gap-2 transition-opacity ${currentCategory === 'green' ? 'opacity-100' : 'opacity-40'}`}>
                                      <span className="w-3 h-3 rounded-full bg-success"></span> High (70-100)
                                    </div>
                                    <div className={`flex items-center gap-2 transition-opacity ${currentCategory === 'yellow' ? 'opacity-100' : 'opacity-40'}`}>
                                      <span className="w-3 h-3 rounded-full bg-warning"></span> Medium (40-70)
                                    </div>
                                    <div className={`flex items-center gap-2 transition-opacity ${currentCategory === 'red' ? 'opacity-100' : 'opacity-40'}`}>
                                      <span className="w-3 h-3 rounded-full bg-destructive"></span> Low (0-40)
                                    </div>
                                  </div>
                                )}
                                {isOverallUtilPct && (
                                  <div className="space-y-1">
                                    <div className="font-semibold mb-1.5">Overall Util %:</div>
                                    <div className={`flex items-center gap-2 transition-opacity ${currentCategory === 'green' ? 'opacity-100' : 'opacity-40'}`}>
                                      <span className="w-3 h-3 rounded-full bg-success"></span> Green: 70–100%
                                    </div>
                                    <div className={`flex items-center gap-2 transition-opacity ${currentCategory === 'yellow' ? 'opacity-100' : 'opacity-40'}`}>
                                      <span className="w-3 h-3 rounded-full bg-warning"></span> Amber: 40–69%
                                    </div>
                                    <div className={`flex items-center gap-2 transition-opacity ${currentCategory === 'red' ? 'opacity-100' : 'opacity-40'}`}>
                                      <span className="w-3 h-3 rounded-full bg-destructive"></span> Red: 0–39%
                                    </div>
                                  </div>
                                )}
                                <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 border-4 border-transparent border-t-background"></div>
                              </div>,
                              document.body
                            )}
                            {/* Tooltip for checkmark - shows conversion target met */}
                            {hoveredCheckmark === idx && checkmarkTooltipPosition && !!row._showCheckmark && createPortal(
                              <div 
                                ref={checkmarkTooltipRef}
                                className="fixed px-3 py-2 bg-background text-white text-xs font-medium rounded-lg shadow-xl pointer-events-none z-float border border-border whitespace-nowrap"
                                style={{
                                  left: `${checkmarkTooltipPosition.x}px`,
                                  top: `${checkmarkTooltipPosition.y + 25}px`,
                                  transform: 'translate(-50%, 0)'
                                }}
                              >
                                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 -mb-1 border-4 border-transparent border-b-background"></div>
                                <div className="text-success font-semibold">
                                  Conversion target met ({row._targetPercentage !== undefined ? `${row._targetPercentage}%` : 'N/A'})
                                </div>
                              </div>,
                              document.body
                            )}
                          </div>
                        ) : column.key === 'feasibility' ? (
                          <span className={`px-2 py-1 rounded text-sm font-semibold ${
                            row[column.key] === 'High' 
                              ? 'bg-success/50 text-success border border-success' 
                              : row[column.key] === 'Medium'
                              ? 'bg-warning/50 text-warning border border-warning'
                              : 'bg-destructive/50 text-destructive border border-destructive'
                          }`}>
                    {row[column.key] as ReactNode}
                          </span>
                        ) : (
                          row[column.key] as ReactNode
                        )}
                  </td>
                    );
                  })}
              </tr>
              {expandedBody != null && (
                <tr>
                  <td colSpan={columns.length} className="px-3 pb-3 pt-0">
                    {expandedBody}
                  </td>
                </tr>
              )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
